import {inject, injectable} from 'inversify';

import type {BaseDomainFactory} from '../Base/BaseDomain.factory';

import {deepClone} from '../../../utils/object';
import {ModelsRegistry} from '../../system/modelsRegistry/ModelsRegistry';
import {ArticleModel} from './Article.model';

@injectable()
export class ArticleFactory
  implements BaseDomainFactory<ArticleModel, ArticleModel.Props>
{
  constructor(
    @inject(ModelsRegistry)
    private readonly modelsRegistry: ModelsRegistry,
  ) {}

  create(props: ArticleModel.Props): ArticleModel {
    props.content = this.processContent(props);

    return this.modelsRegistry.put(new ArticleModel(props));
  }

  processContent(props: ArticleModel.Props): ArticleModel.Props['content'] {
    const fileSourcesLookup = new Map<string, string>();

    props.files.filter(Boolean).forEach(file => {
      fileSourcesLookup.set(file.id, file.originalSrc.url!);
    });

    const contentCopy = deepClone(props.content);

    this.fillInFileSourcesToYcNodes(contentCopy, fileSourcesLookup);

    return contentCopy;
  }

  /**
   * This method fills in the files sources to the content's media (ycMedia) nodes,
   * since for each user the backend gives us different sources.
   */
  private fillInFileSourcesToYcNodes(
    content: ArticleModel.Props['content'],
    sources: Map<string, string>,
  ) {
    content?.content?.forEach(node => {
      if (node.type === 'ycMedia') {
        const maybeSource = sources.get(node.attrs?.fileId);

        if (maybeSource) {
          node.attrs!.src = maybeSource;
        } else {
          // TODO: Handle missing file
        }
      }

      if (node.content) {
        this.fillInFileSourcesToYcNodes(node.content, sources);
      }
    });
  }
}
